import * as THREE from 'three'

/**
 * Camera maths for a locked axonometric view.
 *
 * The frustum is expressed in WORLD UNITS with `zoom` pinned to 1 and
 * `camera.manual = true`. R3F's default orthographic handling sizes the frustum
 * in *pixels* (`±size/2`), which means the visible world changes with the canvas
 * aspect — fatal for an export that must reproduce the on-screen framing at a
 * different resolution. Owning the frustum makes a resize a pure change of
 * resolution.
 *
 * True isometric = camera direction (1,1,1): azimuth exactly 45°, elevation
 * atan(1/√2) = 35.264°, and all three world axes projecting to the same screen
 * length (√(2/3)).
 */
export const ISO_ELEVATION = Math.atan(1 / Math.SQRT2)

export const CAMERA_DISTANCE = 60
/** A negative near plane is legal on an orthographic camera and prevents pan clipping. */
export const CAMERA_NEAR = -200
export const CAMERA_FAR = 400

export type ViewMode = 'iso' | 'plan'
export type ViewIndex = 0 | 1 | 2 | 3

/** Camera offsets for the four isometric corners, as (x, z) signs. */
const CORNERS: readonly (readonly [number, number])[] = [
  [1, 1],
  [-1, 1],
  [-1, -1],
  [1, -1],
]

const PLAN_UP = new THREE.Vector3(0, 0, -1)
const ISO_UP = new THREE.Vector3(0, 1, 0)

/** Unit vector from the target towards the camera. */
export function cameraOffset(mode: ViewMode, viewIndex: ViewIndex, target = new THREE.Vector3()): THREE.Vector3 {
  if (mode === 'plan') return target.set(0, 1, 0)
  // viewIndex is 0|1|2|3 and CORNERS has four entries.
  const [sx, sz] = CORNERS[viewIndex]!
  return target.set(sx, 1, sz).normalize()
}

export function cameraUp(mode: ViewMode): THREE.Vector3 {
  return mode === 'plan' ? PLAN_UP : ISO_UP
}

export interface ViewState {
  mode: ViewMode
  viewIndex: ViewIndex
  /** Target point the camera looks at. */
  cx: number
  cy: number
  cz: number
  /** Half the vertical world extent of the frustum. */
  halfY: number
}

export interface CameraBasis {
  /** Unit vector from the target towards the camera. */
  offset: THREE.Vector3
  /** Direction the camera looks, i.e. from the camera into the scene. */
  forward: THREE.Vector3
  right: THREE.Vector3
  up: THREE.Vector3
}

export function cameraBasis(mode: ViewMode, viewIndex: ViewIndex): CameraBasis {
  const offset = cameraOffset(mode, viewIndex)
  const forward = offset.clone().negate()
  const worldUp = cameraUp(mode)
  const right = new THREE.Vector3().crossVectors(forward, worldUp).normalize()
  const up = new THREE.Vector3().crossVectors(right, forward).normalize()
  return { offset, forward, right, up }
}

/** Writes a `ViewState` onto an orthographic camera. Aspect is width / height. */
export function applyView(
  camera: THREE.OrthographicCamera,
  view: ViewState,
  aspect: number,
): void {
  const basis = cameraBasis(view.mode, view.viewIndex)
  const halfY = view.halfY
  camera.left = -halfY * aspect
  camera.right = halfY * aspect
  camera.top = halfY
  camera.bottom = -halfY
  camera.near = CAMERA_NEAR
  camera.far = CAMERA_FAR
  camera.zoom = 1
  camera.up.copy(cameraUp(view.mode))
  camera.position
    .set(view.cx, view.cy, view.cz)
    .addScaledVector(basis.offset, CAMERA_DISTANCE)
  camera.lookAt(view.cx, view.cy, view.cz)
  camera.updateProjectionMatrix()
  camera.updateMatrixWorld()
}

export interface FitResult {
  cx: number
  cy: number
  cz: number
  halfY: number
}

const _corner = new THREE.Vector3()

/**
 * Frames a set of world-space boxes.
 *
 * Corners of every individual box are projected onto the camera's right/up
 * basis — never the union AABB, whose extreme corners at a 45° azimuth are
 * empty diagonal tips and would zoom far too far out.
 */
export function fitView(
  boxes: readonly THREE.Box3[],
  aspect: number,
  mode: ViewMode,
  viewIndex: ViewIndex,
  padding = 1.08,
): FitResult | null {
  if (boxes.length === 0) return null
  const { right, up } = cameraBasis(mode, viewIndex)

  let minR = Infinity
  let maxR = -Infinity
  let minU = Infinity
  let maxU = -Infinity

  for (const box of boxes) {
    if (box.isEmpty()) continue
    for (const x of [box.min.x, box.max.x]) {
      for (const y of [box.min.y, box.max.y]) {
        for (const z of [box.min.z, box.max.z]) {
          _corner.set(x, y, z)
          const r = _corner.dot(right)
          const u = _corner.dot(up)
          if (r < minR) minR = r
          if (r > maxR) maxR = r
          if (u < minU) minU = u
          if (u > maxU) maxU = u
        }
      }
    }
  }

  if (!Number.isFinite(minR)) return null

  const halfWidth = ((maxR - minR) / 2) * padding
  const halfHeight = ((maxU - minU) / 2) * padding
  const halfY = Math.max(halfHeight, halfWidth / Math.max(aspect, 1e-3), 0.5)

  const centre = right
    .clone()
    .multiplyScalar((minR + maxR) / 2)
    .add(up.clone().multiplyScalar((minU + maxU) / 2))

  return { cx: centre.x, cy: centre.y, cz: centre.z, halfY }
}

/**
 * How far the pan target moves on the floor plane per screen pixel.
 *
 * The screen-up axis is foreshortened onto the ground, so a vertical drag has to
 * travel further in world space than a horizontal one — by exactly
 * 1 / sin(elevation) for the isometric view.
 */
export function panBasis(mode: ViewMode, viewIndex: ViewIndex) {
  const { right, up } = cameraBasis(mode, viewIndex)
  const flatUp = new THREE.Vector3(up.x, 0, up.z)
  const foreshortening = flatUp.length()
  flatUp.normalize()
  return {
    right: new THREE.Vector3(right.x, 0, right.z).normalize(),
    up: flatUp,
    /** Multiplier applied to vertical drag distance. */
    upScale: foreshortening > 1e-4 ? 1 / foreshortening : 1,
  }
}

export const ZOOM_LIMITS = { min: 0.7, max: 24 }

export function clampHalfY(value: number): number {
  return Math.min(ZOOM_LIMITS.max, Math.max(ZOOM_LIMITS.min, value))
}
